import { HttpStatusCode, isAxiosError, isCancel } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { MaybeUndef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

import { toApiError } from './toApiError';

/**
 * Internal sentinel when submit-bid result polling stops (e.g. hook unmounted
 * or request aborted). Not user-facing.
 */
export const SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE =
  '__submit_portal_bid_result_poll_cancelled__';

/**
 * Body for `POST /integrations/portal/submit-bid`
 * (see `fn/api/routes/integrations/submit_portal_bid.go` in drumkit).
 */
export interface SubmitPortalBidRequestBody {
  credentialId: number;
  integrationId: number;
  loadId: string;
  amount: string;
  emailId?: MaybeUndef<number>;
  quoteRequestId?: MaybeUndef<number>;
  // When set, used for integrations whose resolved bid basis is `line_haul`.
  lineHaulAmount?: MaybeUndef<string>;
  // When set, preferred for integrations whose resolved bid basis is `total_sell`.
  totalSellAmount?: MaybeUndef<string>;
  startUrl?: MaybeUndef<string>;
  tmsCustomerId?: MaybeUndef<number>;
  customerName?: MaybeUndef<string>;
  form?: Record<string, unknown>;
  metadata?: Record<string, string>;
  // RFC References header chain for failure-email threading (Gmail/Front/Outlook when populated).
  threadReferences?: MaybeUndef<string>;
  // RFC In-Reply-To message IDs for failure-email threading.
  inReplyTo?: MaybeUndef<string[]>;
}

export interface SubmitPortalBidResponseBody {
  confirmationId?: string;
  confirmationText?: string;
  duration?: string;
  attempts?: number;
  artifacts?: unknown[];
}

export interface SubmitPortalBidStartResponseBody {
  jobId: string;
  status?: string;
}

export interface SubmitPortalBidResultRequestBody {
  jobId: string;
  signal?: AbortSignal;
}

function buildSubmitPortalBidPayload(
  body: SubmitPortalBidRequestBody
): SubmitPortalBidRequestBody {
  const payload: SubmitPortalBidRequestBody = { ...body };

  const trimmedStartUrl = payload.startUrl?.trim();
  if (trimmedStartUrl) {
    payload.startUrl = trimmedStartUrl;
  } else {
    delete payload.startUrl;
  }

  /* Optional failure-email threading headers, trim whitespace and drop the
   * keys entirely when there is nothing useful to send, so the request body
   * does not carry empty strings or empty arrays to the portal submit API.
   */
  const trimmedRefs = payload.threadReferences?.trim();
  if (trimmedRefs) {
    payload.threadReferences = trimmedRefs;
  } else {
    delete payload.threadReferences;
  }

  if (payload.inReplyTo?.length) {
    const ids = payload.inReplyTo
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (ids.length > 0) {
      payload.inReplyTo = ids;
    } else {
      delete payload.inReplyTo;
    }
  } else {
    delete payload.inReplyTo;
  }

  const linehaul = payload.lineHaulAmount?.trim();
  if (linehaul) {
    payload.lineHaulAmount = linehaul;
  } else {
    delete payload.lineHaulAmount;
  }

  const totalSell = payload.totalSellAmount?.trim();
  if (totalSell) {
    payload.totalSellAmount = totalSell;
  } else {
    delete payload.totalSellAmount;
  }

  if (!payload.emailId || payload.emailId <= 0) {
    delete payload.emailId;
  }

  if (!payload.quoteRequestId || payload.quoteRequestId <= 0) {
    delete payload.quoteRequestId;
  }

  return payload;
}

export async function submitPortalBid(
  body: SubmitPortalBidRequestBody
): Promise<Result<SubmitPortalBidStartResponseBody, ApiError>> {
  try {
    const payload = buildSubmitPortalBidPayload(body);
    const { data } = await axios.post<SubmitPortalBidStartResponseBody>(
      '/integrations/portal/submit-bid',
      payload
    );

    const jobId = data?.jobId ?? '';
    if (!jobId) {
      return err({ message: 'Portal submit-bid start response missing jobId' });
    }

    return ok({
      jobId,
      status: data?.status,
    });
  } catch (error) {
    captureException(error, { functionName: 'submitPortalBid' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to submit portal bid' });
    }

    return err(
      toApiError(error, 'Failed to submit portal bid', error.response?.status)
    );
  }
}

export async function getSubmitPortalBidResult(
  request: SubmitPortalBidResultRequestBody
): Promise<Result<SubmitPortalBidResponseBody, ApiError>> {
  const { signal, jobId } = request;
  try {
    const resp = await axios.post<SubmitPortalBidResponseBody>(
      '/integrations/portal/submit-bid/result',
      { jobId },
      signal ? { signal } : undefined
    );

    if (resp.status === HttpStatusCode.Accepted) {
      return err({ message: 'Portal bid still processing', status: 202 });
    }

    return ok(resp.data);
  } catch (error) {
    if (isCancel(error)) {
      return err({ message: SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE });
    }

    captureException(error, { functionName: 'getSubmitPortalBidResult' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to get submit portal bid result' });
    }

    if (error.response?.status === 401) {
      throw error;
    }

    if (error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err(
      toApiError(
        error,
        'Failed to get submit portal bid result',
        error.response?.status
      )
    );
  }
}
